import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowLeft, Banknote, Clock3, RefreshCw, ShieldCheck, UserRound } from "lucide-react";
import { getAdminTransactions, getAdminUsers, getKycCases } from "../lib/api";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { CustomerPricingPanel } from "./CustomerPricingPanel";
import { PageHeading } from "./PageHeading";
import { Skeleton, SkeletonRows } from "./Skeleton";
import { TableEmpty } from "./TableEmpty";
import type { AdminTransaction, AdminUserSummary, KycCase, PortalView, Viewer } from "../types";
import { DEPLOYMENT_NAME } from "../lib/runtime";
import { formatPersonName } from "../lib/directory";
import { kycStage, kycStageInfo } from "../lib/kycOps";

function money(cents: number | null, asset: string): string {
  if (cents === null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: asset || "USD" }).format(cents / 100);
}

const BACK_LABELS: Partial<Record<PortalView, string>> = {
  users: "Back to users",
  kyc: "Back to KYC queue",
  transactions: "Back to transactions"
};

export function CustomerPage({ customerGuid, initialUser, viewer, onNavigate, backTo }: {
  customerGuid: string | null;
  initialUser: AdminUserSummary | null;
  viewer: Viewer;
  onNavigate: (view: PortalView) => void;
  backTo: PortalView;
}) {
  const { timeZone } = useTimeZone();
  // A deep link supplies only the GUID, so the profile is always re-resolved from it.
  const [user, setUser] = useState(initialUser?.customerGuid === customerGuid ? initialUser : null);
  const [kycCase, setKycCase] = useState<KycCase | null>(null);
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [loading, setLoading] = useState(Boolean(customerGuid));
  const [error, setError] = useState<string | null>(null);
  const backLabel = BACK_LABELS[backTo] ?? "Back to users";

  const loadProfile = (guid: string) => {
    setLoading(true); setError(null);
    return Promise.all([
      getAdminUsers({ search: guid }),
      getKycCases(),
      getAdminTransactions({ search: guid })
    ]);
  };

  useEffect(() => {
    if (!customerGuid) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    void Promise.all([
      getAdminUsers({ search: customerGuid }),
      getKycCases(),
      getAdminTransactions({ search: customerGuid })
    ]).then(([userResult, cases, transactionResult]) => {
      if (cancelled) return;
      const resolved = userResult.users.find((entry) => entry.customerGuid === customerGuid) ?? null;
      if (!resolved && !initialUser) setError(`This customer is not available in the current ${DEPLOYMENT_NAME} directory.`);
      setUser(resolved ?? (initialUser?.customerGuid === customerGuid ? initialUser : null));
      setKycCase(cases.find((entry) => entry.customerGuid === customerGuid) ?? null);
      setTransactions(transactionResult.transactions.slice(0, 10));
      setTotalTransactions(transactionResult.totalCount);
    }).catch((reason) => !cancelled && setError(reason instanceof Error ? reason.message : "Unable to load customer profile"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // initialUser is a navigation hint only; the GUID is the identity of this view.
  }, [customerGuid]);

  const totals = useMemo(() => transactions.reduce((result, transaction) => {
    result.amount += transaction.amountCents ?? 0;
    result.fees += transaction.feeCents ?? 0;
    return result;
  }, { amount: 0, fees: 0 }), [transactions]);
  const canViewReferences = true;

  // Refresh re-resolves the same customer without clearing what is already shown.
  async function refresh() {
    if (!customerGuid) return;
    try {
      const [userResult, cases, transactionResult] = await loadProfile(customerGuid);
      setUser(userResult.users.find((entry) => entry.customerGuid === customerGuid) ?? null);
      setKycCase(cases.find((entry) => entry.customerGuid === customerGuid) ?? null);
      setTransactions(transactionResult.transactions.slice(0, 10));
      setTotalTransactions(transactionResult.totalCount);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : "Unable to load customer profile");
    } finally {
      setLoading(false);
    }
  }

  if (!user) {
    return <div className="page-stack">
      <button className="back-button" onClick={() => onNavigate(backTo)}><ArrowLeft size={16} />{backLabel}</button>
      <div className="detail-empty customer-empty">
        <UserRound size={30} />
        <strong>{loading ? "Loading customer…" : "No customer selected"}</strong>
        <span>{loading ? `Resolving the profile from the ${DEPLOYMENT_NAME} directory.` : error ?? "Select a customer from Users or KYC."}</span>
      </div>
    </div>;
  }

  return <div className="page-stack customer-page">
    <button className="back-button" onClick={() => onNavigate(backTo)}><ArrowLeft size={16} />{backLabel}</button>
    <PageHeading
      eyebrow="CUSTOMER PROFILE"
      title={formatPersonName(user.name) ?? "Unnamed user"}
      description={<>{user.emailMasked || "No email available"} · <span className="mono-cell">{user.customerGuid || "No customer GUID"}</span></>}
      actions={<>
        <button className="secondary-button" onClick={() => void refresh()} disabled={loading || !customerGuid}><RefreshCw size={16} className={loading ? "spin" : undefined} aria-hidden="true" />Refresh</button>
        <span className={`status-badge ${user.banned ? "status-rejected" : "status-verified"}`}>{user.banned ? "access banned" : "access active"}</span>
      </>}
    />
    {error ? <div className="error-panel"><AlertCircle size={19} /><div><strong>Partial customer profile</strong><p>{error}</p></div></div> : null}
    <div className="customer-summary-grid">
      <article><span><UserRound size={18} /></span><small>Sign-in access</small><strong>{user.banned ? "Banned" : "Active"}</strong><em>Created {formatTimestamp(user.createdAt, timeZone)}</em></article>
      <article><span><ShieldCheck size={18} /></span><small>Verification</small><strong>{kycStageInfo(kycStage(user.state)).label}</strong><em>{user.state} · started {formatTimestamp(user.identityCreatedAt, timeZone)}</em></article>
      <article><span><Clock3 size={18} /></span><small>Case workflow</small><strong>{(kycCase?.workflowStatus ?? "unassigned").replaceAll("_", " ")}</strong><em>{kycCase?.assigneeEmail || "No owner assigned"}</em></article>
      <article><span><Banknote size={18} /></span><small>Transactions</small><strong>{loading ? <Skeleton width="48px" height="20px" /> : totalTransactions}</strong><em>{money(totals.amount, transactions[0]?.asset || "USD")} in loaded activity</em></article>
    </div>
    <div className="customer-detail-grid">
      <section className="history-card customer-identity-card"><div className="section-heading-row"><h2>Identity &amp; case</h2><button className="table-action" onClick={() => onNavigate("kyc")}>Open KYC queue</button></div><dl className="customer-detail-list">
        <div><dt>Firebase UID</dt><dd>{user.firebaseUid || "—"}</dd></div><div><dt>Customer GUID</dt><dd>{user.customerGuid || "—"}</dd></div>
        <div><dt>Identity verification</dt><dd>{canViewReferences ? user.identityVerificationGuid || "—" : "Role restricted"}</dd></div><div><dt>Persona inquiry</dt><dd>{canViewReferences ? user.personaInquiryId || "—" : "Role restricted"}</dd></div>
        <div><dt>Case priority</dt><dd><span className={`case-priority priority-${kycCase?.priority ?? "normal"}`}>{kycCase?.priority ?? "normal"}</span></dd></div><div><dt>Last case update</dt><dd>{formatTimestamp(kycCase?.updatedAt ?? null, timeZone)}</dd></div>
        <div className="customer-detail-wide"><dt>Latest internal note</dt><dd>{kycCase?.latestNote || "No case notes recorded."}</dd></div>
      </dl></section>
      <aside className="customer-actions-card"><p className="section-label">SAFE ACTIONS</p><h2>Continue investigation</h2><p>Provider truth and sensitive mutations remain in their guarded modules.</p><button className="secondary-button full-button" onClick={() => onNavigate("kyc")}>Manage KYC case</button><button className="secondary-button full-button" onClick={() => onNavigate("transactions")}>Search transaction ledger</button></aside>
    </div>
    {user.customerGuid ? <CustomerPricingPanel customerGuid={user.customerGuid} viewer={viewer} /> : null}
    <section className="history-card"><div className="section-heading-row"><h2>Recent customer transactions</h2><span className="history-count">{loading && transactions.length === 0 ? "Loading…" : `${transactions.length} of ${totalTransactions}`}</span></div><div className="table-wrap"><table aria-busy={loading}><thead><tr><th>Transfer</th><th>Type</th><th>Amount</th><th>Fee</th><th>Status</th><th>Created</th></tr></thead><tbody>
      {loading && transactions.length === 0 ? <SkeletonRows columns={6} /> : null}
                {!loading && transactions.length === 0 ? <TableEmpty columns={6} filtered={false} emptyMessage="No transactions for this customer" /> : null}
      {transactions.map((transaction) => <tr key={transaction.transferId}><td><strong className="cell-title">#{transaction.transferId}</strong><span className="cell-subtitle mono-cell">{transaction.guid}</span></td><td>{transaction.displayType}</td><td><strong>{money(transaction.amountCents, transaction.asset)}</strong></td><td>{money(transaction.feeCents, transaction.asset)}</td><td><span className={`status-badge status-${transaction.status.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{transaction.status}</span></td><td>{formatTimestamp(transaction.createdAt, timeZone)}</td></tr>)}
    </tbody></table></div></section>
  </div>;
}
